import { calcSaleCommission } from './commissionCalc';

/**
 * aggregateReportData — pure aggregation function.
 *
 * Receives the five AppContext arrays/objects and an optional `now` date
 * (defaults to new Date()). Returns a flat object of primitive metrics.
 * No business logic — just counting, filtering, and date math.
 *
 * Accepts an optional `now` parameter so it can be tested deterministically.
 */
export function aggregateReportData(
  { sales = [], leads = [], activities = [], appointments = [], userData = {} },
  now = new Date()
) {
  const month = now.getMonth();
  const year  = now.getFullYear();

  // ── Monthly filtering ────────────────────────────────────────────────────
  const monthlySales = sales.filter(s => {
    const d = new Date(s.soldDate ?? s.date);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const unitsSold = monthlySales.length;

  // ── Commission / gross — replicate AppContext formula exactly ────────────
  const commissionMTD = monthlySales.reduce((a, s) => a + calcSaleCommission(s, userData), 0);
  const totalGrossMTD = monthlySales.reduce((a, s) => a + (s.frontGross ?? 0) + (s.backGross ?? 0), 0);

  // ── Activities ───────────────────────────────────────────────────────────
  const monthlyActivities = activities.filter(a => {
    const d = new Date(a.date ?? a.createdAt);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const callsMade  = monthlyActivities.filter(a => a.type?.toLowerCase() === 'call').length;
  const textsSent  = monthlyActivities.filter(a => a.type?.toLowerCase() === 'text').length;
  const emailsSent = monthlyActivities.filter(a => a.type?.toLowerCase() === 'email').length;

  // ── Leads ────────────────────────────────────────────────────────────────
  const monthlyLeads = leads.filter(l => {
    const d = new Date(l.createdAt);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const leadsContacted = monthlyLeads.length;
  const leadsClosed    = unitsSold;

  // ── Appointments ─────────────────────────────────────────────────────────
  const monthlyAppts = appointments.filter(a => {
    const d = new Date(a.date ?? a.createdAt);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const apptSet    = monthlyAppts.filter(a => a.status === 'set'    || a.set).length;
  const apptShown  = monthlyAppts.filter(a => a.status === 'shown'  || a.shown).length;
  const apptClosed = monthlyAppts.filter(a => a.status === 'closed' || a.closed).length;

  // ── Weekly distribution: Wk1=days 1–7, Wk2=8–14, Wk3=15–21, Wk4=22+ ───
  const weekCounts = [0, 0, 0, 0];
  monthlySales.forEach(s => {
    const day    = new Date(s.soldDate ?? s.date).getDate();
    const bucket = day <= 7 ? 0 : day <= 14 ? 1 : day <= 21 ? 2 : 3;
    weekCounts[bucket]++;
  });
  const [unitsFirstWeek, unitsSecondWeek, unitsThirdWeek, unitsLastWeek] = weekCounts;

  // ── zeroDays: Mon–Sat days from 1st through today with zero activity ─────
  // Sunday (getDay() === 0) is the only excluded day.
  const activityDates = new Set(
    monthlyActivities.map(a => {
      const d = new Date(a.date ?? a.createdAt);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    })
  );
  let zeroDays = 0;
  for (let day = 1; day <= now.getDate(); day++) {
    const d = new Date(year, month, day);
    if (d.getDay() === 0) continue;  // Sundays excluded
    if (!activityDates.has(`${year}-${month}-${day}`)) zeroDays++;
  }

  // ── Touch rates — join by leadId (preferred) or customerName (fallback) ──
  // Join key is internal — never in the return value. No names logged.
  let firstTouchRate  = null;
  let secondTouchRate = null;

  if (leadsContacted > 0) {
    const touchCounts = new Map();
    monthlyActivities.forEach(a => {
      const key = a.leadId ?? a.customerName ?? null;
      if (!key) return;
      touchCounts.set(key, (touchCounts.get(key) ?? 0) + 1);
    });
    const leadKeys = monthlyLeads.map(l => l.id ?? l.customerName).filter(Boolean);
    const withFirst  = leadKeys.filter(k => (touchCounts.get(k) ?? 0) >= 1).length;
    const withSecond = leadKeys.filter(k => (touchCounts.get(k) ?? 0) >= 2).length;
    firstTouchRate  = Math.round((withFirst  / leadsContacted) * 100);
    secondTouchRate = Math.round((withSecond / leadsContacted) * 100);
  }

  // ── Selling days math ────────────────────────────────────────────────────
  const SELLING_DAYS = 22;
  const daysInMonth  = new Date(year, month + 1, 0).getDate();
  const dayFraction  = now.getDate() / daysInMonth;
  const sellingDaysElapsed   = Math.round(dayFraction * SELLING_DAYS);
  const sellingDaysRemaining = SELLING_DAYS - sellingDaysElapsed;

  return {
    repName:        userData?.name ?? '',
    reportingMonth: now.toLocaleString('default', { month: 'long' }),
    reportingYear:  year,
    unitsSold,
    monthlyGoal:    userData?.monthlyGoal ?? 0,
    incomeGoal:     userData?.incomeGoal  ?? null,
    commissionMTD,
    totalGrossMTD,
    callsMade,
    textsSent,
    emailsSent,
    leadsContacted,
    leadsClosed,
    apptSet,
    apptShown,
    apptClosed,
    unitsFirstWeek,
    unitsSecondWeek,
    unitsThirdWeek,
    unitsLastWeek,
    zeroDays,
    firstTouchRate,
    secondTouchRate,
    sellingDaysElapsed,
    sellingDaysRemaining,
  };
}
